import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismBanner,
  PrismButton,
  PrismButtonGroup,
  PrismCard,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import type { IconBrandName } from '@mylighthouse/prism-ember/types/components/prism/icon-brand';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import type { Ota } from 'frontend-utils/models';
import type { PaRatePreference } from 'frontend-utils/models/hotel';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import type HotelSettingsPreferredRateRoute from 'lumen/routes/hotel/settings/preferred-rate';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { PrimaSettingsInProgressRatePreference } from 'lumen/services/pricing-assistant-data-request-service';
import {
  MEALTYPE_ICONS,
  MEALTYPE_TRANSLATION_KEYS,
  type MealTypeValues,
  ROOMTYPE_TRANSLATION_KEYS,
} from 'lumen/utils/constants/general-constants';
import {
  PREFERRED_CHANNEL_STATUSES,
  PREFERRED_RATE_MIN_DAYS_BETWEEN_EDITING,
  RATE_TYPE_TO_TRANSLATION_KEYS,
  RATE_TYPES_ICONS,
} from 'lumen/utils/constants/pricing-assistant-constants';
import { preferredChannelInProgressName } from 'lumen/utils/pricing-assistant/preferred-channel-utils';
import {
  isMinDaysBetweenEditingElapsed,
  timeUntilNextAllowedUpdate,
} from 'lumen/utils/pricing-assistant/preferred-rate-editing-utils';
import type { ModelFrom } from 'lumen/utils/routes';

interface SettingsPreferredRatePreferredRateSignature {
  Args: {
    hasUserChangedPreferredRateSettingsValue: boolean;
    model: ModelFrom<HotelSettingsPreferredRateRoute>;
    updateHasUserChangedPreferredRateSettingsValue: (boolean: boolean) => void;
  };
}

type ChannelOption = Option & { id: string; order: number; status: string };

export default class SettingsPreferredRatePreferredRate extends Component<SettingsPreferredRatePreferredRateSignature> {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked arePreferredRateSettingsValid: boolean | null = true;

  // Make a local copy of the current Pricing Assistant Preferred rate settings. This local copy will be used to render the state of the page and to store the user's changes before saving.
  @tracked localSettingsCopy: PaRatePreference = JSON.parse(
    JSON.stringify(this.paPreferredRateSettings),
  ) as PaRatePreference;

  launchPreferredRateSimulateTask = task(
    { restartable: true },
    async (): Promise<void> => {
      try {
        // Note: this is a task so that it can deal with cancellation better
        // -> this.model.launchPreferredRateSimulateTask is a restartable task and can be triggered multiple times in a short window
        // More info: https://ember-concurrency.com/docs/task-cancelation-help
        const apiResponse =
          await this.args.model.launchRatePreferenceSimulateRequestTask.perform(
            this.localSettingsCopy,
          );

        if (apiResponse.isSuccess) {
          this.arePreferredRateSettingsValid =
            apiResponse.arePreferredRateSettingsValid;
        }
      } catch (error) {
        this.errorTracking.captureException(error);
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get arePreferredRateSettingsUpdating() {
    return this.args.model.saveAndUpdatePreferredRateSettings.isRunning;
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get channelOptions() {
    const options: ChannelOption[] = [];

    Object.entries(
      this.hotelAndMarketService.hotel.recommendationsChannels,
    ).forEach(([channel, status]) => {
      if (status !== PREFERRED_CHANNEL_STATUSES.IN_PROGRESS) {
        const ota = this.store.peekRecord<Ota>('ota', channel)!;
        const option: ChannelOption = {
          id: channel,
          label: ota.name,
          status,
          iconBrand: (ota.id as IconBrandName | null) ?? undefined,
          order: ota.order,
        };

        if (status === PREFERRED_CHANNEL_STATUSES.UNSUPPORTED) {
          option.disabled = true;
          option.badge = this.intl.t(
            'pricing_assistant.preferred_rate.settings.label.not_available',
          );
          option.badgeType = 'neutral';
        }

        options.push(option);
      }
    });

    options.sort((a, b) => a['order'] - b['order']);

    return options;
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get guestOptions() {
    if (!this.paRatePreferencesOptions?.persons) {
      return [];
    }

    return this.paRatePreferencesOptions?.persons.map((guest: number) => ({
      id: guest,
      label: this.intl.t('rates.persons_selection', { count: guest }),
    }));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDiscardButtonDisabled() {
    return (
      !this.args.hasUserChangedPreferredRateSettingsValue ||
      this.isFormDisabled ||
      this.arePreferredRateSettingsUpdating
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isFormDisabled() {
    return this.isUserViewer || !this.isMinDaysBetweenEditingElapsed;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMinDaysBetweenEditingElapsed() {
    return isMinDaysBetweenEditingElapsed(
      this.paPreferredRateSettings.last_edited,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSaveButtonDisabled() {
    return (
      !this.args.hasUserChangedPreferredRateSettingsValue ||
      this.isFormDisabled ||
      !this.arePreferredRateSettingsValid ||
      this.arePreferredRateSettingsUpdating ||
      this.launchPreferredRateSimulateTask.isRunning
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isUserViewer() {
    return this.access
      .isPricingAssistantPreferredRateSettingsActionOrInputDisabled;
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get losOptions() {
    if (!this.paRatePreferencesOptions?.los) {
      return [];
    }

    return this.paRatePreferencesOptions?.los.map((count: number) => ({
      id: count,
      label: this.intl.t('rates.nights', { count }),
    }));
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get mealtypeOptions() {
    if (!this.paRatePreferencesOptions?.mealtype) {
      return [];
    }

    return this.paRatePreferencesOptions?.mealtype.map(
      (mealtype: MealTypeValues) => ({
        id: mealtype,
        icon: MEALTYPE_ICONS[mealtype],
        label: this.intl.t(MEALTYPE_TRANSLATION_KEYS[mealtype]),
      }),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get paPreferredRateSettings() {
    return this.hotelAndMarketService.hotel.paRatePreference;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get paRatePreferencesOptions() {
    return this.args.model
      .requestPricingAssistantRatePreferencesOptionsTaskResult.value;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get preferredChannelInfoSubText() {
    // the supported subtext gets priority over the in progress subtext because when the user selects a supported channel they need to be informed.
    if (
      this.selectedChannel?.['status'] === PREFERRED_CHANNEL_STATUSES.SUPPORTED
    ) {
      return this.intl.t(
        'pricing_assistant.preferred_rate.settings.info.preferred_channel',
      );
    }
    if (this.preferredChannelInProgressName) {
      return this.intl.t(
        'pricing_assistant.preferred_rate.settings.info.preferred_channel_in_progress',
        { channel: this.preferredChannelInProgressName },
      );
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get preferredChannelInProgressName() {
    return preferredChannelInProgressName(
      this.hotelAndMarketService.hotel.recommendationsChannels,
      this.store,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get preferredRateTypeOptions() {
    // Extra check for the "value" to prevent a hard crash in case BE returns a rate type that is not supported by FE and thus doesn't have a mapped translation key
    return this.paRatePreferencesOptions?.rateType.map((rateType) => ({
      id: rateType,
      label: RATE_TYPE_TO_TRANSLATION_KEYS[rateType]
        ? this.intl.t(RATE_TYPE_TO_TRANSLATION_KEYS[rateType])
        : rateType,
      icon: RATE_TYPES_ICONS[rateType],
    }));
  }

  get ratePreferenceSettingsInProgress():
    | PrimaSettingsInProgressRatePreference
    | undefined {
    return this.args.model.primaSettingsInProgressTaskResult.value
      ?.rate_preference;
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get roomTypeOptions() {
    if (!this.paRatePreferencesOptions?.roomType) {
      return [];
    }

    return this.paRatePreferencesOptions?.roomType.map((roomType) => {
      const roomTypeLabel = ROOMTYPE_TRANSLATION_KEYS[roomType.id]
        ? this.intl.t(ROOMTYPE_TRANSLATION_KEYS[roomType.id])
        : roomType.name;
      return { id: roomType.id, label: roomTypeLabel };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedChannel() {
    return this.channelOptions.find(
      (channelOption) => channelOption['id'] === this.localSettingsCopy.channel,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedGuests() {
    return this.guestOptions?.find(
      (guestOption) => guestOption.id === this.localSettingsCopy.persons,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedLos() {
    return this.losOptions?.find(
      (losOption) => losOption.id === this.localSettingsCopy.los,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedMealtype() {
    return this.mealtypeOptions?.find(
      (mealtypeOption) => mealtypeOption.id === this.localSettingsCopy.mealtype,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedRoomType() {
    return this.roomTypeOptions?.find(
      (roomTypeOption) =>
        roomTypeOption.id === this.localSettingsCopy.room_type.id,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get timeUntilNextAllowedUpdate() {
    return timeUntilNextAllowedUpdate(this.paPreferredRateSettings.last_edited);
  }

  @action resetLocalSettingsCopy(): void {
    this.updateLocalSettingsCopy(
      JSON.parse(
        JSON.stringify(this.paPreferredRateSettings),
      ) as PaRatePreference,
    );

    // Reset our naive change detection when resetting the local copy (+ also triggered when leaving the route or discarding changes)
    this.args.updateHasUserChangedPreferredRateSettingsValue(false);
  }

  @action
  async saveCurrentPreferredRateSettings(): Promise<void> {
    const isConfirmed = await this.alerting.showConfirmWarningAlert({
      title: this.intl.t(
        'pricing_assistant.preferred_rate.settings.confirm_alert_title',
      ),
      text: this.intl.t(
        'pricing_assistant.preferred_rate.settings.confirm_alert_text',
        {
          count: PREFERRED_RATE_MIN_DAYS_BETWEEN_EDITING,
        },
      ),
      buttons: {
        cancel: {
          text: this.intl.t('general.cancel'),
        },
        confirm: {
          text: this.intl.t('general.confirm'),
        },
      },
    });

    if (isConfirmed) {
      if (!this.isUserViewer) {
        this.usageAnalytics.track('Base Rate Settings Changed', {
          eventTrigger: 'base-rate-settings',
          product: 'pricing-manager',
        });

        let result = null;

        result =
          await this.args.model.saveAndUpdatePreferredRateSettings.perform(
            this.localSettingsCopy,
          );

        if (result.isSuccess) {
          // Update the local settings copy since the save was successful
          this.resetLocalSettingsCopy();

          //  After sucessful save, transition to the pricing manager page
          this.router.transitionTo('hotel.pricing-manager');

          this.toast.success(
            this.intl
              .t(
                'pricing_assistant.preferred_rate.settings.confirm_toast_message',
              )
              .toString(),
            {
              showIcon: true,
              timeout: TOAST_TIMEOUT_LONG,
            },
          );
        }
      }
    }
  }

  @action selectNewChannel(newChannel: ChannelOption): void {
    this.localSettingsCopy.channel = newChannel['id'];
    this.updateLocalSettingsCopy();
  }

  @action selectNewGuests(newGuests: Option & { id: number }): void {
    this.localSettingsCopy.persons = newGuests['id'];
    this.updateLocalSettingsCopy();
  }

  @action selectNewLos(newLos: Option & { id: number }): void {
    this.localSettingsCopy.los = newLos['id'];
    this.updateLocalSettingsCopy();
  }

  @action selectNewMealtype(newMealtype: Option & { id: number }): void {
    this.localSettingsCopy.mealtype = newMealtype['id'];
    this.updateLocalSettingsCopy();
  }

  @action selectNewRateType(newRateType: string | string[]): void {
    if (Array.isArray(newRateType)) {
      return;
    }
    this.localSettingsCopy.rate_type = newRateType;
    this.updateLocalSettingsCopy();
  }

  @action selectNewRoomType(newRoomType: Option): void {
    // @ts-expect-error: Incorrect type
    this.localSettingsCopy.room_type =
      this.paRatePreferencesOptions?.roomType.find(
        (roomType) => roomType.id === newRoomType['id'],
      );

    this.updateLocalSettingsCopy();
  }

  @action updateLocalSettingsCopy(
    updatedSettingsObject: PaRatePreference | null = null,
  ): void {
    // PRICING ASSISTANT TODO: investigate further if we can use trackedObject here (we're dealing with nested objects/...)

    // localSettingsCopy is tracked, so we use a reassignment here
    if (updatedSettingsObject) {
      this.localSettingsCopy = updatedSettingsObject;
    } else {
      // eslint-disable-next-line no-self-assign
      this.localSettingsCopy = this.localSettingsCopy;
    }

    if (!this.args.hasUserChangedPreferredRateSettingsValue) {
      this.args.updateHasUserChangedPreferredRateSettingsValue(true);
    }

    const isChannelSettingUnchanged =
      this.paPreferredRateSettings.channel === this.localSettingsCopy.channel;
    const isLosSettingUnchanged =
      this.paPreferredRateSettings.los === this.localSettingsCopy.los;
    const isPersonsSettingUnchanged =
      this.paPreferredRateSettings.persons === this.localSettingsCopy.persons;

    // We do not simulate recommendations if one of the following conditions is met:
    // 1. Simulate endpoint does not support channel change. Therefore, we only simulate recommendations if channel setting is not changed
    // 2. When the LOS changes a liveshop needs to happen so we do not simulate recommendations in this scenario.
    // 3. When the persons setting changes a liveshop needs to happen (for every OTA incl. Booking.com) so we do not simulate recommendations in this scenario.
    if (
      isChannelSettingUnchanged &&
      isLosSettingUnchanged &&
      isPersonsSettingUnchanged
    ) {
      // Simulate recommendations in case the user updates a setting. In case, no recommendations are produced, show the invalid settings banner
      void this.launchPreferredRateSimulateTask.perform();
    }
  }

  <template>
    <SettingsPageTemplate
      @description={{t "settings.preferred_rate.description"}}
      @hasAccountSelector={{true}}
      @title={{t "pricing_assistant.settings.navigation.label.preferred_rate"}}
      @type="container"
    >
      <div>
        <PrismCard @contentLayout="noPadding" class="preferred-rate-card">
          <:content>
            <div class="preferred-rate-card__content p-400">
              {{#if this.isUserViewer}}
                <PrismBanner
                  @bannerMessage={{t
                    "pricing_assistant.preferred_rate.settings.viewer_banner"
                  }}
                />
              {{else if (not this.isMinDaysBetweenEditingElapsed)}}
                <PrismBanner
                  @bannerMessage={{t
                    "pricing_assistant.preferred_rate.settings.last_edit_banner"
                    timeUntilEdit=this.timeUntilNextAllowedUpdate
                  }}
                />
              {{else if (not this.arePreferredRateSettingsValid)}}
                <PrismBanner
                  @bannerMessage={{t
                    "pricing_assistant.preferred_rate.settings.invalid_settings_banner"
                  }}
                  @bannerStyle="critical"
                />
              {{/if}}

              <div class="preferred-rate-card__setting">
                <div class="flex flex-row items-center gap-50">
                  <p class="text-200--bold">{{t
                      "pricing_assistant.preferred_rate.settings.label.channel"
                    }}</p>
                  <GeneralIconWithTooltip
                    @color="inherit"
                    @side="right"
                    @size="extra-small"
                    @tooltipContent={{t
                      "pricing_assistant.preferred_rate.settings.label.channel.tooltip"
                    }}
                  />
                </div>
                <PrismSelectInput
                  @disabled={{this.isFormDisabled}}
                  @isSecondary={{true}}
                  @onChange={{this.selectNewChannel}}
                  @options={{this.channelOptions}}
                  @unsafe_matchTriggerWidth={{true}}
                  @value={{this.selectedChannel}}
                />
                {{#if this.preferredChannelInfoSubText}}
                  <p
                    class="text-200--regular preferred-rate-card__info-text"
                  >{{this.preferredChannelInfoSubText}}</p>
                {{/if}}
              </div>

              <div class="preferred-rate-card__setting">
                <p class="text-200--bold">{{t "rates.type"}}</p>
                <PrismButtonGroup
                  @isDisabled={{this.isFormDisabled}}
                  @onChange={{this.selectNewRateType}}
                  @value={{this.localSettingsCopy.rate_type}}
                  as |ButtonGroup|
                >
                  {{#each this.preferredRateTypeOptions as |rateType|}}
                    <ButtonGroup.button
                      @iconBefore={{rateType.icon}}
                      @label={{rateType.label}}
                      @value={{rateType.id}}
                      class="w-full"
                    />
                  {{/each}}
                </PrismButtonGroup>
              </div>

              <div class="preferred-rate-card__setting">
                <p class="text-200--bold">{{t "rates.length_of_stay"}}</p>
                <PrismSelectInput
                  @disabled={{this.isFormDisabled}}
                  @isSecondary={{true}}
                  @onChange={{this.selectNewLos}}
                  @options={{this.losOptions}}
                  @unsafe_matchTriggerWidth={{true}}
                  @value={{this.selectedLos}}
                />
                {{#if this.ratePreferenceSettingsInProgress.los}}
                  <p
                    class="text-200--regular preferred-rate-card__info-text"
                    data-test-id="los-in-progress-info"
                  >
                    {{t
                      "pricing_assistant.preferred_rate.settings.info.los_in_progress"
                      los=this.ratePreferenceSettingsInProgress.los
                    }}
                  </p>
                {{/if}}
              </div>

              <div class="preferred-rate-card__setting">
                <p class="text-200--bold">{{t "rates.guests"}}</p>
                <PrismSelectInput
                  @disabled={{this.isFormDisabled}}
                  @isSecondary={{true}}
                  @onChange={{this.selectNewGuests}}
                  @options={{this.guestOptions}}
                  @unsafe_matchTriggerWidth={{true}}
                  @value={{this.selectedGuests}}
                />
              </div>

              <div class="preferred-rate-card__setting">
                <p class="text-200--bold">{{t "rates.changes.roomtype"}}</p>
                <PrismSelectInput
                  @disabled={{this.isFormDisabled}}
                  @isSecondary={{true}}
                  @onChange={{this.selectNewRoomType}}
                  @options={{this.roomTypeOptions}}
                  @unsafe_matchTriggerWidth={{true}}
                  @value={{this.selectedRoomType}}
                />
              </div>

              <div class="preferred-rate-card__setting">
                <p class="text-200--bold">{{t
                    "pricing_assistant.settings.panel.section.rates.meal_type"
                  }}</p>
                <PrismSelectInput
                  @disabled={{this.isFormDisabled}}
                  @isSecondary={{true}}
                  @onChange={{this.selectNewMealtype}}
                  @options={{this.mealtypeOptions}}
                  @unsafe_matchTriggerWidth={{true}}
                  @value={{this.selectedMealtype}}
                />
              </div>
            </div>
          </:content>

          <:footer>
            <div class="autopilot-card__footer gap-300">
              <PrismButton
                @buttonType="secondary"
                @isDisabled={{this.isDiscardButtonDisabled}}
                @label={{t "pricing_assistant.settings.footer.secondary_btn"}}
                @onClick={{this.resetLocalSettingsCopy}}
              />
              <PrismButton
                @isDisabled={{this.isSaveButtonDisabled}}
                @label={{t "general.save"}}
                @onClick={{this.saveCurrentPreferredRateSettings}}
              />
            </div>
          </:footer>
        </PrismCard>
      </div>
    </SettingsPageTemplate>
  </template>
}
